import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Zap,
  Trophy,
  Crown,
  Medal,
  Star,
  Plus,
  Flame,
  Target,
  Clock,
  Users,
  Package,
  Sparkles,
  PartyPopper,
  Award,
  CheckCircle2,
  Timer,
  RefreshCw,
  Gift,
  Settings,
  Edit2,
  Trash2,
  Save,
  X,
  DollarSign,
  TrendingUp,
  Calendar,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

const MOTIVATIONAL_MESSAGES = [
  "You're on fire! Keep crushing it!",
  "Every sale is a step towards greatness!",
  "Champions never stop! Keep going!",
  "Your hard work is paying off!",
  "Making it happen, one sale at a time!",
  "You're unstoppable today!",
  "Success loves speed - you're nailing it!",
  "Top performer alert! Keep the momentum!"
];

const COLORS = ['#6366f1', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6'];

const TodaySalesPage = () => {
  const { api, user, isAdmin, isManager } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [offers, setOffers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [celebrationMessage, setCelebrationMessage] = useState('');
  const [showCelebration, setShowCelebration] = useState(false);
  
  // Offer Management State
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  const [offerFormMode, setOfferFormMode] = useState('add'); // 'add' or 'edit'
  const [currentOffer, setCurrentOffer] = useState({ name: '', id: null });
  const [offerToDelete, setOfferToDelete] = useState(null);
  
  // Sale Edit State
  const [showEditSaleDialog, setShowEditSaleDialog] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [saleToDelete, setSaleToDelete] = useState(null);

  const isManagerOrAdmin = isAdmin() || isManager();

  const fetchData = useCallback(async () => {
    try {
      const [salesRes, offersRes] = await Promise.all([
        api.get('/today-sales'),
        api.get('/today-sales/offers')
      ]);
      setData(salesRes.data);
      setOffers(offersRes.data.offers || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Quick sale logging - 1 click!
  const handleQuickSale = async (offerName) => {
    setSubmitting(true);
    try {
      await api.post('/today-sales', { offer_name: offerName, customer_name: '', notes: '' });
      toast.success(`Sale logged for ${offerName}! Great job!`);
      
      // Show celebration
      setCelebrationMessage(MOTIVATIONAL_MESSAGES[Math.floor(Math.random() * MOTIVATIONAL_MESSAGES.length)]);
      setShowCelebration(true);
      setTimeout(() => setShowCelebration(false), 2500);
      
      fetchData();
    } catch (error) {
      toast.error('Failed to log sale');
    } finally {
      setSubmitting(false);
    }
  };

  // Offer CRUD
  const handleSaveOffer = async () => {
    if (!currentOffer.name.trim()) {
      toast.error('Please enter offer name');
      return;
    }
    
    setSubmitting(true);
    try {
      if (offerFormMode === 'add') {
        await api.post('/today-sales/offers', { name: currentOffer.name.trim() });
        toast.success('Offer added successfully');
      } else {
        await api.put(`/today-sales/offers/${currentOffer.id}`, { name: currentOffer.name.trim() });
        toast.success('Offer updated successfully');
      }
      setShowOfferDialog(false);
      setCurrentOffer({ name: '', id: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save offer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteOffer = async () => {
    if (!offerToDelete) return;
    
    try {
      await api.delete(`/today-sales/offers/${offerToDelete.id}`);
      toast.success('Offer deleted');
      setOfferToDelete(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete offer');
    }
  };

  // Sale Edit/Delete
  const handleUpdateSale = async () => {
    if (!editingSale) return;
    
    setSubmitting(true);
    try {
      await api.put(`/today-sales/${editingSale.id}`, {
        offer_name: editingSale.offer_name,
        customer_name: editingSale.customer_name || '',
        notes: editingSale.notes || ''
      });
      toast.success('Sale updated successfully');
      setShowEditSaleDialog(false);
      setEditingSale(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update sale');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSale = async () => {
    if (!saleToDelete) return;
    
    try {
      await api.delete(`/today-sales/${saleToDelete.id}`);
      toast.success('Sale entry removed');
      setSaleToDelete(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete sale');
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <RefreshCw className="w-10 h-10 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-500 font-medium">Loading today's sales...</p>
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg">
          <p className="font-semibold text-slate-900">{payload[0].name}</p>
          <p className="text-indigo-600 font-bold">{payload[0].value} sales</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6" data-testid="today-sales-page">
      {/* Celebration Overlay */}
      {showCelebration && (
        <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
          <div className="animate-bounce text-center">
            <PartyPopper className="w-20 h-20 text-amber-500 mx-auto mb-4" />
            <p className="text-2xl font-bold text-slate-900 bg-white/90 px-6 py-3 rounded-2xl shadow-xl">
              {celebrationMessage}
            </p>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-orange-500 via-pink-500 to-purple-600 p-6 md:p-8 text-white shadow-xl">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjA1Ii8+PC9nPjwvc3ZnPg==')] opacity-30"></div>
        
        <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Zap className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
                <Flame className="w-7 h-7 text-amber-300 animate-pulse" />
                Today's Sales
              </h1>
              <p className="text-white/80 text-sm md:text-base mt-1">{data?.date_display || 'Loading...'}</p>
              <p className="text-white/60 text-xs mt-0.5">Resets daily at 5:00 AM EST</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Big Total Count */}
            <div className="text-right">
              <div className="flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-amber-300" />
                <span className="text-5xl md:text-6xl font-bold tabular-nums">{data?.total_count || 0}</span>
              </div>
              <p className="text-white/80 text-sm">Total Sales Today</p>
            </div>
            
            {/* Refresh Button */}
            <Button 
              variant="outline" 
              size="icon"
              onClick={fetchData}
              className="h-12 w-12 bg-white/10 border-white/20 text-white hover:bg-white/20"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-5 h-5" />
            </Button>
          </div>
        </div>
        
        {/* Motivational Line */}
        <div className="mt-4 pt-4 border-t border-white/20">
          <p className="text-white/90 text-center text-sm md:text-base italic">
            "{MOTIVATIONAL_MESSAGES[Math.floor(data?.total_count || 0) % MOTIVATIONAL_MESSAGES.length]}"
          </p>
        </div>
      </div>

      {/* Offer Buttons Section - Quick 1-Click Sale Logging */}
      <Card className="border-0 shadow-xl overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <DollarSign className="w-5 h-5" />
              Quick Sale - Click to Log
            </CardTitle>
            {isManagerOrAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setOfferFormMode('add');
                  setCurrentOffer({ name: '', id: null });
                  setShowOfferDialog(true);
                }}
                className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                data-testid="manage-offers-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Manage Offers
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {offers.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {offers.map((offer, index) => (
                <div key={offer.id} className="relative group">
                  <Button
                    onClick={() => handleQuickSale(offer.name)}
                    disabled={submitting}
                    className={`w-full h-auto py-4 px-3 flex flex-col items-center gap-2 text-white shadow-lg hover:scale-105 transition-all duration-200 ${
                      submitting ? 'opacity-50' : ''
                    }`}
                    style={{ 
                      background: `linear-gradient(135deg, ${COLORS[index % COLORS.length]}, ${COLORS[(index + 1) % COLORS.length]})` 
                    }}
                    data-testid={`offer-btn-${index}`}
                  >
                    <Package className="w-6 h-6" />
                    <span className="text-sm font-semibold text-center leading-tight line-clamp-2">
                      {offer.name}
                    </span>
                    {submitting && (
                      <RefreshCw className="w-4 h-4 animate-spin absolute top-2 right-2" />
                    )}
                  </Button>
                  
                  {/* Edit/Delete buttons for Admin/Manager */}
                  {isManagerOrAdmin && (
                    <div className="absolute -top-2 -right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOfferFormMode('edit');
                          setCurrentOffer({ name: offer.name, id: offer.id });
                          setShowOfferDialog(true);
                        }}
                        className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white hover:bg-blue-600 shadow"
                        data-testid={`edit-offer-${index}`}
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOfferToDelete(offer);
                        }}
                        className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600 shadow"
                        data-testid={`delete-offer-${index}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
              
              {/* Add Offer Button for Admin/Manager */}
              {isManagerOrAdmin && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setOfferFormMode('add');
                    setCurrentOffer({ name: '', id: null });
                    setShowOfferDialog(true);
                  }}
                  className="w-full h-auto py-4 px-3 flex flex-col items-center gap-2 border-dashed border-2 hover:bg-slate-50"
                  data-testid="add-offer-btn"
                >
                  <Plus className="w-6 h-6 text-slate-400" />
                  <span className="text-sm font-medium text-slate-500">Add Offer</span>
                </Button>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Package className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-slate-500">No offers configured yet</p>
              {isManagerOrAdmin && (
                <Button
                  onClick={() => {
                    setOfferFormMode('add');
                    setCurrentOffer({ name: '', id: null });
                    setShowOfferDialog(true);
                  }}
                  className="mt-4"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Offer
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Full Width Performance Details Table */}
      <Card className="border-0 shadow-xl overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white py-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingUp className="w-5 h-5" />
              Performance Details
              <span className="text-xs font-normal text-white/70 ml-2">
                (Sorted by highest sales)
              </span>
            </CardTitle>
            <div className="flex items-center gap-2 text-white/80 text-sm">
              <Users className="w-4 h-4" />
              <span>{data?.agent_summary?.length || 0} Agents</span>
              <span className="mx-2">•</span>
              <Package className="w-4 h-4" />
              <span>{data?.total_count || 0} Total Sales</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {data?.agent_summary?.length > 0 && offers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left p-4 font-semibold text-slate-700 border-b whitespace-nowrap w-[200px]">
                      <div className="flex items-center gap-2">
                        <span>#</span>
                        <span>Agent Name</span>
                      </div>
                    </th>
                    {offers.map((offer, i) => (
                      <th 
                        key={offer.id} 
                        className="text-center p-2 font-semibold text-slate-700 border-b"
                        style={{ minWidth: '80px' }}
                      >
                        <div className="flex flex-col items-center gap-1">
                          <div 
                            className="w-4 h-4 rounded-full flex-shrink-0" 
                            style={{ backgroundColor: COLORS[i % COLORS.length] }}
                          />
                          <span className="text-xs leading-tight font-medium text-center break-words">
                            {offer.name}
                          </span>
                        </div>
                      </th>
                    ))}
                    <th className="text-center p-4 font-bold text-slate-900 border-b bg-gradient-to-r from-indigo-100 to-purple-100 whitespace-nowrap min-w-[100px]">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.agent_summary.map((agent, agentIndex) => {
                    // Create a map of offer counts for this agent
                    const offerCounts = {};
                    agent.offers.forEach(o => {
                      offerCounts[o.name] = o.count;
                    });
                    
                    return (
                      <tr 
                        key={agent.agent_id}
                        className={`border-b hover:bg-slate-50/80 transition-colors ${
                          agentIndex === 0 ? 'bg-gradient-to-r from-amber-50 to-yellow-50' :
                          agentIndex === 1 ? 'bg-gradient-to-r from-slate-50 to-gray-50' :
                          agentIndex === 2 ? 'bg-gradient-to-r from-orange-50 to-amber-50' : ''
                        }`}
                      >
                        <td className="p-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
                              agentIndex === 0 ? 'bg-gradient-to-br from-amber-400 to-yellow-500 text-white' :
                              agentIndex === 1 ? 'bg-gradient-to-br from-slate-400 to-gray-500 text-white' :
                              agentIndex === 2 ? 'bg-gradient-to-br from-orange-400 to-amber-500 text-white' :
                              'bg-slate-200 text-slate-600'
                            }`}>
                              {agentIndex < 3 ? (
                                agentIndex === 0 ? <Crown className="w-4 h-4" /> :
                                agentIndex === 1 ? <Medal className="w-4 h-4" /> :
                                <Award className="w-4 h-4" />
                              ) : (
                                agentIndex + 1
                              )}
                            </div>
                            <div>
                              <p className={`font-semibold ${
                                agentIndex === 0 ? 'text-amber-700' :
                                agentIndex === 1 ? 'text-slate-700' :
                                agentIndex === 2 ? 'text-orange-700' :
                                'text-slate-900'
                              }`}>
                                {agent.name}
                              </p>
                            </div>
                          </div>
                        </td>
                        {offers.map((offer, i) => {
                          const count = offerCounts[offer.name] || 0;
                          return (
                            <td key={offer.id} className="text-center p-3">
                              {count > 0 ? (
                                <span 
                                  className="inline-flex items-center justify-center min-w-[32px] h-8 px-3 rounded-full text-white font-bold text-sm shadow-sm"
                                  style={{ backgroundColor: COLORS[i % COLORS.length] }}
                                >
                                  {count}
                                </span>
                              ) : (
                                <span className="text-slate-300 text-lg">-</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="text-center p-4 bg-slate-50/50">
                          <span className={`inline-flex items-center justify-center min-w-[40px] h-9 px-4 rounded-full text-white font-bold text-base shadow-md ${
                            agentIndex === 0 ? 'bg-gradient-to-r from-amber-500 to-yellow-500' :
                            agentIndex === 1 ? 'bg-gradient-to-r from-slate-500 to-gray-500' :
                            agentIndex === 2 ? 'bg-gradient-to-r from-orange-500 to-amber-500' :
                            'bg-gradient-to-r from-indigo-500 to-purple-500'
                          }`}>
                            {agent.count}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {/* Total Row */}
                  <tr className="bg-gradient-to-r from-slate-100 to-slate-200 font-bold">
                    <td className="p-4 text-slate-900">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
                          <Trophy className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-lg">Team Total</span>
                      </div>
                    </td>
                    {offers.map((offer, i) => {
                      const total = data.offer_summary?.find(o => o.name === offer.name)?.count || 0;
                      return (
                        <td key={offer.id} className="text-center p-3">
                          <span className={`font-bold text-lg ${total > 0 ? 'text-slate-700' : 'text-slate-400'}`}>
                            {total}
                          </span>
                        </td>
                      );
                    })}
                    <td className="text-center p-4 bg-gradient-to-r from-indigo-200 to-purple-200">
                      <span className="text-indigo-800 font-bold text-2xl">{data.total_count || 0}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center text-slate-500">
              <TrendingUp className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-medium">No sales recorded yet today</p>
              <p className="text-sm mt-1">Click an offer button above to log your first sale!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
              <Target className="w-5 h-5" />
            </div>
            <div>
              <p className="text-3xl font-bold tabular-nums">{data?.total_count || 0}</p>
              <p className="text-sm text-white/80">Total Today</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <p className="text-3xl font-bold tabular-nums">{data?.agent_summary?.length || 0}</p>
              <p className="text-sm text-white/80">Active Agents</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
              <Package className="w-5 h-5" />
            </div>
            <div>
              <p className="text-3xl font-bold tabular-nums">{data?.offer_summary?.length || 0}</p>
              <p className="text-sm text-white/80">Offers Sold</p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
              <Trophy className="w-5 h-5" />
            </div>
            <div>
              <p className="text-2xl font-bold tabular-nums truncate">
                {data?.agent_summary?.[0]?.name?.split(' ')[0] || '-'}
              </p>
              <p className="text-sm text-white/80">Top Performer</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sales Log - For Admin/Manager to Edit/Delete Sales */}
      {isManagerOrAdmin && (
        <Card className="border-0 shadow-xl overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-slate-700 to-slate-800 text-white py-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Clock className="w-5 h-5" />
                Sales Log
                <span className="text-xs font-normal text-white/70 ml-2">
                  (Edit or delete entries to correct mistakes)
                </span>
              </CardTitle>
              <span className="text-sm text-white/80">{data?.recent_sales?.length || 0} entries</span>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {data?.recent_sales?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-3 font-semibold text-slate-700 border-b">Time</th>
                      <th className="text-left p-3 font-semibold text-slate-700 border-b">Agent</th>
                      <th className="text-left p-3 font-semibold text-slate-700 border-b">Offer</th>
                      <th className="text-left p-3 font-semibold text-slate-700 border-b">Customer</th>
                      <th className="text-left p-3 font-semibold text-slate-700 border-b">Notes</th>
                      <th className="text-center p-3 font-semibold text-slate-700 border-b">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_sales.map((sale, index) => (
                      <tr key={sale.id} className="border-b hover:bg-slate-50 transition-colors">
                        <td className="p-3 whitespace-nowrap text-slate-600">
                          {new Date(sale.created_at).toLocaleTimeString('en-US', { 
                            hour: '2-digit', 
                            minute: '2-digit',
                            hour12: true 
                          })}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Avatar className="w-7 h-7">
                              <AvatarFallback className="bg-indigo-100 text-indigo-600 text-xs font-bold">
                                {getInitials(sale.agent_name)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium text-slate-900">{sale.agent_name}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
                            {sale.offer_name}
                          </span>
                        </td>
                        <td className="p-3 text-slate-600">
                          {sale.customer_name || <span className="text-slate-400">-</span>}
                        </td>
                        <td className="p-3 text-slate-600 max-w-[200px] truncate">
                          {sale.notes || <span className="text-slate-400">-</span>}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingSale({
                                  id: sale.id,
                                  offer_name: sale.offer_name,
                                  customer_name: sale.customer_name || '',
                                  notes: sale.notes || ''
                                });
                                setShowEditSaleDialog(true);
                              }}
                              className="h-8 px-3 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              data-testid={`edit-sale-btn-${index}`}
                            >
                              <Edit2 className="w-3.5 h-3.5 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setSaleToDelete(sale)}
                              className="h-8 px-3 text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-sale-btn-${index}`}
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-slate-500">
                <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No sales logged yet today</p>
                <p className="text-sm mt-1">Sales will appear here once agents start logging</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Motivational Footer */}
      <div className="text-center py-4">
        <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full">
          <Gift className="w-5 h-5 text-purple-600" />
          <p className="text-sm font-medium text-purple-700">
            Every sale counts! Keep pushing for greatness!
          </p>
          <Sparkles className="w-5 h-5 text-indigo-600" />
        </div>
      </div>

      {/* Offer Add/Edit Dialog */}
      <Dialog open={showOfferDialog} onOpenChange={setShowOfferDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {offerFormMode === 'add' ? (
                <>
                  <Plus className="w-5 h-5 text-indigo-600" />
                  Add New Offer
                </>
              ) : (
                <>
                  <Edit2 className="w-5 h-5 text-blue-600" />
                  Edit Offer
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Offer Name *</Label>
              <Input 
                placeholder="Enter offer name (e.g., Credit Builder IQ)"
                value={currentOffer.name}
                onChange={(e) => setCurrentOffer({...currentOffer, name: e.target.value})}
                data-testid="offer-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOfferDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveOffer} disabled={submitting} data-testid="save-offer-btn">
              {submitting ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {offerFormMode === 'add' ? 'Add Offer' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Offer Confirmation */}
      <AlertDialog open={!!offerToDelete} onOpenChange={() => setOfferToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              Delete Offer
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{offerToDelete?.name}"? This action cannot be undone.
              Existing sales records with this offer will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteOffer} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Sale Dialog */}
      <Dialog open={showEditSaleDialog} onOpenChange={setShowEditSaleDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-blue-600" />
              Edit Sale Entry
            </DialogTitle>
          </DialogHeader>
          {editingSale && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Offer *</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={editingSale.offer_name}
                  onChange={(e) => setEditingSale({...editingSale, offer_name: e.target.value})}
                  data-testid="edit-sale-offer-select"
                >
                  {offers.map(offer => (
                    <option key={offer.id} value={offer.name}>{offer.name}</option>
                  ))}
                  {/* Include the current offer if it's not in the list */}
                  {!offers.find(o => o.name === editingSale.offer_name) && (
                    <option value={editingSale.offer_name}>{editingSale.offer_name}</option>
                  )}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Customer Name (Optional)</Label>
                <Input 
                  placeholder="Enter customer name"
                  value={editingSale.customer_name}
                  onChange={(e) => setEditingSale({...editingSale, customer_name: e.target.value})}
                  data-testid="edit-sale-customer-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Input 
                  placeholder="Any quick notes..."
                  value={editingSale.notes}
                  onChange={(e) => setEditingSale({...editingSale, notes: e.target.value})}
                  data-testid="edit-sale-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditSaleDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateSale} disabled={submitting} data-testid="update-sale-btn">
              {submitting ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Sale Confirmation */}
      <AlertDialog open={!!saleToDelete} onOpenChange={() => setSaleToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-500" />
              Remove Sale Entry
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this sale entry by {saleToDelete?.agent_name}?
              This is typically used to correct mistakes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSale} className="bg-red-600 hover:bg-red-700">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TodaySalesPage;
